import { EMPTY_ARRAY } from '../../../utils';
import { ButtonVariants } from '../../Button';
import { Box } from '../../Core';
import { PanelActions, PanelHeader } from '../../Panel';
import { Text } from '../../Text';
import type { BaseEntityAdminProps, EntityAdminRecord, HierarchicalColumnProps } from '../types';
import { resolveAllowAddEntity } from '../utils';
import { EntityAdminNewEntityButton, EntityAdminPanelActionButtons } from './EntityAdminActionButtons';
import { useEntityAdminHeaderLayout } from './EntityAdminHeaderLayoutContext';

export interface EntityAdminHeaderProps<
  TRecord extends EntityAdminRecord,
  TDrawerRecord extends EntityAdminRecord,
> extends Pick<
  BaseEntityAdminProps<TRecord, TDrawerRecord>,
  'title' | 'subtitle' | 'entityName' | 'panelActions' | 'drawerOptions' | 'drawerType' | 'allowAddEntity'
> {
  /** The open entity drawer function. */
  openEntityAdminDrawer: HierarchicalColumnProps<TRecord>['openEntityAdminDrawer'];
}

export const EntityAdminHeader = <TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord>({
  drawerOptions,
  entityName = 'Entity',
  title = `${entityName} Page Title`,
  subtitle,
  panelActions = EMPTY_ARRAY,
  allowAddEntity,
  openEntityAdminDrawer,
}: EntityAdminHeaderProps<TRecord, TDrawerRecord>) => {
  const { isPanelLayout } = useEntityAdminHeaderLayout();
  const allowAddMain = resolveAllowAddEntity(allowAddEntity).main;

  // Spaces / dealer panels hide the page header; New and panelActions live in the blotter extras menu.
  if (isPanelLayout) {
    return null;
  }

  return (
    <PanelHeader alignItems="center" flex="0 0 auto" minHeight="auto">
      <Box>
        {title && <h2 data-testid="entity-admin-page-title">{title}</h2>}
        {subtitle && (
          <Box mt="spacing8">
            <Text whiteSpace="break-spaces" data-testid="entity-admin-page-subtitle">
              {subtitle}
            </Text>
          </Box>
        )}
      </Box>
      <PanelActions>
        <EntityAdminPanelActionButtons panelActions={panelActions} honorVariant />
        {allowAddMain ? (
          <EntityAdminNewEntityButton
            entityName={entityName}
            disabled={drawerOptions == null}
            variant={ButtonVariants.Positive}
            onClick={() => openEntityAdminDrawer({ setupEntities: EMPTY_ARRAY, addingChildEntity: false })}
          />
        ) : null}
      </PanelActions>
    </PanelHeader>
  );
};
